import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { loginUser, setupFounderPassword, getFounderNotes, createFounderNote, toggleFounderNote, changeFounderPassword } from '../api';
import './FounderWorkspace.css';

const VALID_FOUNDERS = ['demian', 'maycol', 'linda'];

const FounderWorkspace = () => {
  const { founderName } = useParams();
  const name = founderName.toLowerCase();

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  // Estados para el modal de cambio de clave
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const [notes, setNotes] = useState([]);
  const [newForms, setNewForms] = useState({
    todo: { content: '' },
    mentoria: { content: '', student_email: '' },
    nota: { content: '' }
  });

  const isValidRoute = VALID_FOUNDERS.includes(name);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await getFounderNotes(name);
      setNotes(res.data);
    } catch (err) {
      console.error("Error fetching notes", err);
    }
  }, [name]);

  useEffect(() => {
    if (!isValidRoute) return;
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const currentUser = localStorage.getItem('userName');
    
    if (token && role === 'founder' && currentUser === name) {
      setIsAuthorized(true);
      fetchNotes();
    }
  }, [isValidRoute, name, fetchNotes]);

  // --- NUEVO: CERRAR SESIÓN AL CERRAR PESTAÑA O ACTUALIZAR ---
  useEffect(() => {
    const handleUnload = () => {
      if (isAuthorized) {
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        localStorage.removeItem('userRole');
      }
    };
    window.addEventListener('unload', handleUnload);
    return () => window.removeEventListener('unload', handleUnload);
  }, [isAuthorized]);

  if (!isValidRoute) {
    return <Navigate to="/" />;
  }

  const handleAuth = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    try {
      let res;
      if (isSetupMode) {
        res = await setupFounderPassword({ name, password });
      } else {
        res = await loginUser({ email: name, password });
      }
      
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('userName', res.data.user_name);
      localStorage.setItem('userRole', res.data.user_role);
      setIsAuthorized(true);
      fetchNotes();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Error de autenticación");
    }
  };

  // --- NUEVO: FUNCIÓN CERRAR SESIÓN MANUAL ---
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userRole');
    setIsAuthorized(false);
    window.location.reload();
  };

  // --- NUEVO: FUNCIÓN CAMBIAR CONTRASEÑA ---
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 3) {
      alert("La contraseña debe tener al menos 3 caracteres.");
      return;
    }
    try {
      await changeFounderPassword(name, newPassword);
      alert("✅ ¡Contraseña actualizada con éxito! Usa esta clave la próxima vez que ingreses.");
      setIsPasswordModalOpen(false);
      setNewPassword('');
    } catch (err) {
      alert("❌ Error al cambiar la contraseña.");
    }
  };

  const handleAddNote = async (type) => {
    const form = newForms[type];
    if (!form.content.trim()) return;

    try {
      await createFounderNote(name, {
        note_type: type,
        content: form.content,
        student_email: form.student_email || null
      });
      setNewForms({ ...newForms, [type]: { content: '', student_email: '' } });
      fetchNotes();
    } catch (err) {
      alert("Error al guardar");
    }
  };

  const handleToggleTodo = async (noteId, currentStatus) => {
    try {
      await toggleFounderNote(noteId, !currentStatus);
      fetchNotes();
    } catch (err) {
      console.error(err);
    }
  };

  const formatTime = (utcDateStr) => {
    const tz = name === 'demian' ? 'America/Mexico_City' : 'America/Lima';
    return new Intl.DateTimeFormat('es-ES', { 
      timeZone: tz, 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    }).format(new Date(utcDateStr));
  };

  if (!isAuthorized) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <h2>Bóveda de {name.charAt(0).toUpperCase() + name.slice(1)}</h2>
          <p>{isSetupMode ? "Configura tu contraseña maestra por primera vez." : "Ingresa tu contraseña maestra."}</p>
          <form onSubmit={handleAuth}>
            <input
              type="password"
              className="admin-login-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              required
            />
            {errorMsg && <p className="admin-error-msg">{errorMsg}</p>}
            <button type="submit" className="admin-login-btn">
              {isSetupMode ? "Guardar y Entrar" : "Ingresar"}
            </button>
          </form>
          <button 
            onClick={() => setIsSetupMode(!isSetupMode)} 
            style={{background:'none', border:'none', color:'#58A6FF', marginTop:'15px', cursor:'pointer'}}
          >
            {isSetupMode ? "¿Ya tienes clave? Inicia sesión" : "¿Es tu primera vez? Configura tu clave"}
          </button>
        </div>
      </div>
    );
  }

  const todos = notes.filter(n => n.note_type === 'todo');
  const mentorias = notes.filter(n => n.note_type === 'mentoria');
  const notasLibres = notes.filter(n => n.note_type === 'nota');

  return (
    <div className="founder-container">
      
      {/* HEADER ACTUALIZADO CON BOTONES */}
      <div className="founder-header">
        <div>
          <h1>Workspace / {name}</h1>
          <div className="timezone-badge" style={{marginTop: '10px', display: 'inline-block'}}>
            <i className="far fa-clock"></i> Zona Horaria: {name === 'demian' ? 'México (CDMX)' : 'Perú (Lima)'}
          </div>
        </div>
        
        <div className="header-actions">
          <button onClick={() => setIsPasswordModalOpen(true)} className="btn-outline blue">
            <i className="fas fa-key"></i> Cambiar Clave
          </button>
          <button onClick={handleLogout} className="btn-outline red">
            <i className="fas fa-sign-out-alt"></i> Cerrar Sesión
          </button>
        </div>
      </div>

      <div className="workspace-grid">
        
        {/* COLUMNA 1: TO-DO LIST */}
        <div className="workspace-column">
          <h2><i className="fas fa-check-square"></i> To-Do List</h2>
          <div className="notes-list">
            {todos.map(n => (
              <div key={n.id} className={`note-card ${n.is_completed ? 'completed' : ''}`}>
                <span className="note-timestamp">{formatTime(n.created_at)}</span>
                <div style={{display:'flex', alignItems:'flex-start'}}>
                  <input 
                    type="checkbox" 
                    className="checkbox-todo" 
                    checked={n.is_completed} 
                    onChange={() => handleToggleTodo(n.id, n.is_completed)} 
                  />
                  <p className="note-content">{n.content}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="note-form">
            <textarea 
              className="note-input" 
              rows="2" 
              placeholder="Nueva tarea..."
              value={newForms.todo.content}
              onChange={(e) => setNewForms({...newForms, todo: {content: e.target.value}})}
            />
            <button className="btn-add-note" onClick={() => handleAddNote('todo')}>Agregar Tarea</button>
          </div>
        </div>

        {/* COLUMNA 2: MENTORÍAS */}
        <div className="workspace-column">
          <h2><i className="fas fa-chalkboard-teacher"></i> Registro de Mentorías</h2>
          <div className="notes-list">
            {mentorias.map(n => (
              <div key={n.id} className="note-card">
                <span className="note-timestamp">{formatTime(n.created_at)}</span>
                <p className="note-content">{n.content}</p>
                {n.student_email && <span className="note-student"><i className="fas fa-user"></i> {n.student_email}</span>}
              </div>
            ))}
          </div>
          <div className="note-form">
            <input 
              type="email" 
              className="note-input" 
              placeholder="Correo del alumno (Opcional)"
              value={newForms.mentoria.student_email}
              onChange={(e) => setNewForms({...newForms, mentoria: {...newForms.mentoria, student_email: e.target.value}})}
            />
            <textarea 
              className="note-input" 
              rows="2" 
              placeholder="Detalles de la mentoría..."
              value={newForms.mentoria.content}
              onChange={(e) => setNewForms({...newForms, mentoria: {...newForms.mentoria, content: e.target.value}})}
            />
            <button className="btn-add-note" onClick={() => handleAddNote('mentoria')}>Registrar Mentoría</button>
          </div>
        </div>

        {/* COLUMNA 3: NOTAS LIBRES */}
        <div className="workspace-column">
          <h2><i className="fas fa-sticky-note"></i> Notas Libres</h2>
          <div className="notes-list">
            {notasLibres.map(n => (
              <div key={n.id} className="note-card">
                <span className="note-timestamp">{formatTime(n.created_at)}</span>
                <p className="note-content">{n.content}</p>
              </div>
            ))}
          </div>
          <div className="note-form">
            <textarea 
              className="note-input" 
              rows="3" 
              placeholder="Escribe una nota..."
              value={newForms.nota.content}
              onChange={(e) => setNewForms({...newForms, nota: {content: e.target.value}})}
            />
            <button className="btn-add-note" onClick={() => handleAddNote('nota')}>Guardar Nota</button>
          </div>
        </div>

      </div>

      {/* MODAL CAMBIAR CONTRASEÑA */}
      {isPasswordModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Cambiar Contraseña</h3>
              <button onClick={() => setIsPasswordModalOpen(false)} className="btn-close">×</button>
            </div>
            <form onSubmit={handleChangePassword}>
              <div style={{marginBottom: '15px'}}>
                <label style={{display: 'block', marginBottom: '5px', color: '#C9D1D9'}}>Nueva Contraseña</label>
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  required 
                  placeholder="Escribe tu nueva clave..."
                  style={{width: '100%', padding: '10px', backgroundColor: '#0D1117', border: '1px solid #30363D', borderRadius: '4px', color: 'white', boxSizing: 'border-box'}}
                />
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setIsPasswordModalOpen(false)} className="btn-cancel">Cancelar</button>
                <button type="submit" className="btn-save">Actualizar Clave</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default FounderWorkspace;